import RPMDateTimePickerWithPlaceholder from '@/components/RPMDateTimePicker/RPMDateTimePickerWithPlaceholder';
import StyledModal from '@/components/StyledModal';
import { DAY, WEEKDAYS } from '@/constants/calendar';
import { CustomRecurrenceData } from '@/gql/events/types';
import { getRepeatEveryFrequency } from '@/utils/events';
import rem from '@/utils/rem';
import {
  Box,
  Button,
  HStack,
  ModalBody,
  ModalFooter,
  NumberInput,
  NumberInputField,
  Radio,
  RadioGroup,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useCallback, useState } from 'react';

import CustomRecurrenceMenu from './CustomRecurrenceMenu';

interface CustomRecurrenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CustomRecurrenceData) => void;
  recurrence?: string;
  interval?: number;
  occurrences?: number;
  byDays?: { day: string }[];
  currentDate?: Date;
}

const CustomRecurrenceModal = ({
  isOpen,
  onClose,
  onSave,
  recurrence,
  interval,
  occurrences,
  byDays,
  currentDate,
}: CustomRecurrenceModalProps) => {
  const [selectedRecurrence, setSelectedRecurrence] = useState<string>(
    getRepeatEveryFrequency(recurrence || 'week', interval || 1),
  );
  const [selectedValue, setSelectedValue] = useState('Never');
  const [scheduledDate, setScheduledDate] = useState<Date | null>(currentDate || null);
  const [selectedDays, setSelectedDays] = useState<string[]>(
    byDays?.length ? byDays.map((d) => d.day) : currentDate ? [WEEKDAYS[currentDate.getDay()]] : [],
  );
  const [selectedInterval, setSelectedInterval] = useState<string>(interval + '');
  const [selectedNumberOfOccurrences, setSelectedNumberOfOccurrences] = useState<string>((occurrences ?? 13) + '');

  const {
    isOpen: isDatePickerOpen,
    onOpen: onDatePickerOpen,
    onClose: onDatePickerClose,
  } = useDisclosure({ id: 'datepicker' });

  const handleScheduledDate = useCallback(
    (date: Date) => {
      setScheduledDate(date);
    },
    [scheduledDate],
  );

  const onClickUnplan = useCallback(() => {
    setScheduledDate(null);
    onDatePickerClose();
  }, [onDatePickerClose]);

  const onDatePickerSave = useCallback(() => {
    onDatePickerClose();
  }, [onDatePickerClose]);

  const toggleDaySelection = (day: string) => {
    setSelectedDays((prevSelectedDays) => {
      const defaultDays = currentDate ? [WEEKDAYS[currentDate.getDay()]] : [];

      const isDefault =
        prevSelectedDays.length === defaultDays.length && prevSelectedDays.every((d) => defaultDays.includes(d));

      if (isDefault && defaultDays.length === 1 && defaultDays.includes(day)) {
        return prevSelectedDays;
      }

      if (prevSelectedDays.includes(day)) {
        const updated = prevSelectedDays.filter((d) => d !== day);
        return updated.length === 0 ? defaultDays : updated;
      }

      return [...prevSelectedDays, day];
    });
  };

  const handleSubmit = () => {
    onSave({
      selectedWeekDays: selectedRecurrence === 'Week' ? selectedDays.map((day) => ({ day })) : [],
      interval: +selectedInterval,
      selectedRecurrence,
      ...(selectedValue === 'On' && scheduledDate && { scheduledDate: scheduledDate }),
      ...(selectedValue === 'After' && { occurrences: +selectedNumberOfOccurrences }),
    });
  };

  return (
    <StyledModal isOpen={isOpen} onClose={onClose} title="Repeat" size="md">
      <ModalBody minHeight={rem(80)} paddingTop={rem(8)}>
        <VStack alignItems="stretch" justifyContent="space-between" gap={rem(20)}>
          <HStack gap={rem(8)}>
            <Text flex="0" flexBasis={rem(78)}>
              Every
            </Text>
            <NumberInput defaultValue={selectedInterval} max={100} min={0} onChange={setSelectedInterval} step={1}>
              <NumberInputField
                width={rem(48)}
                height={rem(28)}
                marginRight={rem(10)}
                padding={0}
                textAlign="center"
                border={`${rem(1)} solid`}
                borderColor="stroke-primary"
                borderRadius={rem(4)}
                _hover={{
                  boxShadow: 'none',
                }}
                _focusVisible={{
                  borderColor: 'cyan.400',
                }}
                backgroundColor="button-bg-secondary"
              />
            </NumberInput>
            <CustomRecurrenceMenu
              variant="bordered"
              recurrence={selectedRecurrence}
              setSelectedRecurrence={setSelectedRecurrence}
              readonly={false}
            />
          </HStack>
          {selectedRecurrence === 'Week' && (
            <HStack gap={rem(8)}>
              <Text flex="0" flexBasis={rem(78)}>
                On
              </Text>
              <HStack gap={rem(8)}>
                {WEEKDAYS.map((day) => {
                  const isSelected = selectedDays.includes(day);

                  return (
                    <Button
                      key={day}
                      alignItems="center"
                      justifyContent="center"
                      display="flex"
                      width={rem(34)}
                      height={rem(34)}
                      color={isSelected ? 'blue.1000' : 'text-primary'}
                      fontWeight={500}
                      textAlign="center"
                      textTransform="uppercase"
                      border={`${rem(2)} solid`}
                      borderColor={isSelected ? 'transparent' : 'stroke-primary'}
                      borderRadius="50%"
                      _hover={{
                        backgroundColor: isSelected ? 'blue.600' : 'background-secondary',
                        borderColor: 'transparent',
                      }}
                      backgroundColor={isSelected ? 'text-primary' : 'transparent'}
                      onClick={() => toggleDaySelection(day)}
                      size="sm"
                    >
                      {day.slice(0, 1)}
                    </Button>
                  );
                })}
              </HStack>
            </HStack>
          )}
          <Text flexBasis={rem(10)} fontWeight="bold">
            Ends
          </Text>
          <RadioGroup onChange={setSelectedValue} value={selectedValue}>
            <VStack alignItems="flex-start" gap={rem(25)}>
              <Radio
                border="none"
                _focusVisible={{
                  borderColor: 'transparent',
                }}
                _checked={{
                  backgroundColor: 'skyblue',
                  border: '3px solid #142737',
                }}
                colorScheme="blue"
                outlineColor="white"
                outlineOffset="revert"
                value="Never"
              >
                <Text>Never</Text>
              </Radio>
              <HStack width="full">
                <Radio
                  marginRight={rem(79)}
                  border="none"
                  _focusVisible={{
                    borderColor: 'transparent',
                  }}
                  _checked={{
                    backgroundColor: 'skyblue',
                    border: '3px solid #142737',
                  }}
                  colorScheme="blue"
                  outlineColor="white"
                  outlineOffset="revert"
                  value="On"
                >
                  <Text>On</Text>
                </Radio>
                <Box position="relative" width="full" opacity={selectedValue === 'On' ? 1 : 0.5}>
                  <Box
                    position="absolute"
                    zIndex={selectedValue === 'On' ? 0 : 2}
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    background="transparent"
                    cursor={selectedValue === 'On' ? '' : 'not-allowed'}
                  />
                  <RPMDateTimePickerWithPlaceholder
                    onDatePickerOpen={onDatePickerOpen}
                    isDatePickerOpen={isDatePickerOpen}
                    onSave={onDatePickerSave}
                    onCancel={onClickUnplan}
                    placeholder="Choose Date"
                    onClickUnplan={onClickUnplan}
                    scheduledDate={scheduledDate}
                    tabIndex={DAY}
                    updateSelectedDate={handleScheduledDate}
                    customWidth={'full'}
                  />
                </Box>
              </HStack>
              <HStack>
                <Radio
                  marginRight={rem(70)}
                  border="none"
                  _focusVisible={{
                    borderColor: 'transparent',
                  }}
                  _checked={{
                    backgroundColor: 'skyblue',
                    border: '3px solid #142737',
                  }}
                  colorScheme="blue"
                  outlineColor="white"
                  outlineOffset="revert"
                  value="After"
                >
                  <Text>After</Text>
                </Radio>
                <NumberInput
                  defaultValue={selectedNumberOfOccurrences}
                  max={100}
                  min={0}
                  onChange={setSelectedNumberOfOccurrences}
                  step={1}
                >
                  <NumberInputField
                    width={rem(48)}
                    height={rem(28)}
                    marginRight={rem(8)}
                    padding={0}
                    textAlign="center"
                    border={`${rem(1)} solid`}
                    borderColor="stroke-primary"
                    borderRadius={rem(4)}
                    _hover={{
                      boxShadow: 'none',
                    }}
                    _focusVisible={{
                      borderColor: 'cyan.400',
                    }}
                    backgroundColor="button-bg-secondary"
                    disabled={selectedValue != 'After'}
                  />
                </NumberInput>
                <Text>Times</Text>
              </HStack>
            </VStack>
          </RadioGroup>
        </VStack>
      </ModalBody>
      <ModalFooter justifyContent="flex-end" gap={rem(16)} display="flex">
        <HStack gap={rem(16)}>
          <Button onClick={onClose} size="md" variant="secondary">
            Cancel
          </Button>
          <Button isLoading={false} onClick={handleSubmit} size="md" type="submit" variant="primary">
            Done
          </Button>
        </HStack>
      </ModalFooter>
    </StyledModal>
  );
};

export default CustomRecurrenceModal;
